import React, { useState, useRef, useEffect } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Send, Zap, Paperclip, Mic, ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, Kanban, Target, Search } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { PIPELINE_DATA, PIPELINE_CHAT_RESPONSES } from '../../data/pipelineData';
import V4AdHocChat from './V4AdHocChat';
import { DAVID_PIPELINE_DD, DAVID_FORECAST_DD, DAVID_LOSS_REASONS_DD, DAVID_PIPELINE_KEYPRESS } from '../../data/davidPipelineDDData';

const AreaSparkline = ({ data, color, alert, width = 80, height = 32 }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data); const max = Math.max(...data); const range = max - min || 1;
  const pad = 2; const w = width - pad * 2; const h = height - pad * 2;
  const points = data.map((v, i) => ({ x: pad + (i / (data.length - 1)) * w, y: pad + h - ((v - min) / range) * h }));
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const strokeColor = alert ? '#f87171' : color || '#818cf8';
  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[points.length-1].x} cy={points[points.length-1].y} r={2.5} fill={strokeColor} />
    </svg>
  );
};

const VelocityCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const sparkColor = item.trendUp ? '#34d399' : '#f87171';
  return (
    <div className={`${cardBg} border ${item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/60') : border} rounded-xl px-3.5 py-3`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-[10px] ${ts}`}>{item.label}</p>
          <p className={`text-lg font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
          <div className="flex items-center gap-1 mt-0.5">
            {item.trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span className={`text-[10px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
          </div>
        </div>
        <AreaSparkline data={item.data} color={sparkColor} alert={item.alert} />
      </div>
    </div>
  );
};

const StageFunnel = ({ stages, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const colors = [
    { bar: isDark ? 'bg-indigo-500/30' : 'bg-indigo-200', text: isDark ? 'text-indigo-400' : 'text-indigo-600' },
    { bar: isDark ? 'bg-blue-500/30' : 'bg-blue-200', text: isDark ? 'text-blue-400' : 'text-blue-600' },
    { bar: isDark ? 'bg-cyan-500/30' : 'bg-cyan-200', text: isDark ? 'text-cyan-400' : 'text-cyan-600' },
    { bar: isDark ? 'bg-amber-500/30' : 'bg-amber-200', text: isDark ? 'text-amber-400' : 'text-amber-600' },
    { bar: isDark ? 'bg-emerald-500/30' : 'bg-emerald-200', text: isDark ? 'text-emerald-400' : 'text-emerald-600' },
  ];
  const maxVal = Math.max(...stages.map(s => s.value));
  return (
    <div className="space-y-2">
      {stages.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={`text-[9px] w-20 text-right truncate ${ts}`}>{s.name}</span>
          <div className="flex-1 h-5 rounded-md overflow-hidden" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
            <div className={`h-full rounded-md ${colors[i].bar} flex items-center px-2 transition-all duration-500`} style={{ width: `${Math.max((s.value / maxVal) * 100, 8)}%` }}>
              <span className={`text-[9px] font-bold ${colors[i].text}`}>${(s.value / 1000).toFixed(0)}K</span>
            </div>
          </div>
          <div className="text-right w-16">
            <span className={`text-[10px] font-bold ${tp}`}>{s.count}</span>
            <span className={`text-[9px] ml-1 ${ts}`}>deals</span>
          </div>
          <span className={`text-[9px] w-10 text-right ${ts}`}>{s.conversionRate}</span>
        </div>
      ))}
    </div>
  );
};

const ForecastBar = ({ forecast, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const total = forecast.committed.value + forecast.bestCase.value + forecast.pipeline.value;
  const targetPct = (forecast.target / total) * 100;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className={`text-[11px] font-semibold ${tp}`}>Forecast vs. Target</p>
        <span className={`text-[10px] ${ts}`}>Target: ${(forecast.target / 1000).toFixed(0)}K</span>
      </div>
      <div className="relative h-6 rounded-lg overflow-hidden flex" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
        <div className="h-full bg-emerald-500/40 flex items-center justify-center" style={{ width: `${(forecast.committed.value / total) * 100}%` }}>
          <span className={`text-[8px] font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>${(forecast.committed.value / 1000).toFixed(0)}K</span>
        </div>
        <div className="h-full bg-amber-500/30 flex items-center justify-center" style={{ width: `${(forecast.bestCase.value / total) * 100}%` }}>
          <span className={`text-[8px] font-bold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>${(forecast.bestCase.value / 1000).toFixed(0)}K</span>
        </div>
        <div className="h-full bg-indigo-500/20 flex items-center justify-center" style={{ width: `${(forecast.pipeline.value / total) * 100}%` }}>
          <span className={`text-[8px] font-bold ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>${(forecast.pipeline.value / 1000).toFixed(0)}K</span>
        </div>
        <div className="absolute top-0 bottom-0 w-px bg-white/60" style={{ left: `${targetPct}%` }} />
      </div>
      <div className="flex items-center gap-4 mt-1.5">
        <span className={`text-[9px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-emerald-500/40" /> Committed</span>
        <span className={`text-[9px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-amber-500/30" /> Best Case</span>
        <span className={`text-[9px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-indigo-500/20" /> Pipeline</span>
      </div>
    </div>
  );
};

const LostReasonsChart = ({ reasons, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  return (
    <div className="space-y-1.5">
      {reasons.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className={`text-[9px] w-28 truncate ${tp}`}>{r.reason}</span>
          <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
            <div className="h-full rounded-full bg-gradient-to-r from-red-500/40 to-amber-500/30 transition-all duration-500" style={{ width: `${r.pct}%` }} />
          </div>
          <span className={`text-[9px] font-medium w-6 text-right ${tp}`}>{r.pct}%</span>
        </div>
      ))}
    </div>
  );
};

const V4SalesPipeline = ({ isDark, onWorkflowToggle, workflowPanelOpen, activeUser, pipelineAdHocScenario, onPipelineAdHocDone }) => {
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [overviewDD, setOverviewDD] = useState(false);
  const [forecastDD, setForecastDD] = useState(false);
  const [lossReasonsDD, setLossReasonsDD] = useState(false);
  const messagesEndRef = useRef(null);

  const isDavid = activeUser?.id === 'sales';

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || PIPELINE_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Sally about pipeline..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto' }} data-testid="pipeline-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.sally} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Sally</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="pipeline-send-btn">
              <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Pipeline Overview DD (top-right, David-only) ── */
  if (overviewDD && isDavid) {
    const s = DAVID_PIPELINE_DD.overview;
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={s.id}
        scenarios={{ [s.id]: s }}
        backLabel="Back to Pipeline"
        onBack={() => setOverviewDD(false)}
      />
    );
  }

  /* ── Forecast card DD (David-only) ── */
  if (forecastDD && isDavid) {
    const s = DAVID_FORECAST_DD.forecast;
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={s.id}
        scenarios={{ [s.id]: s }}
        backLabel="Back to Pipeline"
        onBack={() => setForecastDD(false)}
      />
    );
  }

  /* ── Loss Reasons card DD (David-only) ── */
  if (lossReasonsDD && isDavid) {
    const s = DAVID_LOSS_REASONS_DD.reasons;
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={s.id}
        scenarios={{ [s.id]: s }}
        backLabel="Back to Pipeline"
        onBack={() => setLossReasonsDD(false)}
      />
    );
  }

  /* ── Keypress A (David-only) ── */
  if (pipelineAdHocScenario && isDavid) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={pipelineAdHocScenario}
        scenarios={DAVID_PIPELINE_KEYPRESS}
        backLabel="Back to Pipeline"
        onBack={() => onPipelineAdHocDone?.()}
        skipFirstUserDelay
      />
    );
  }

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="pipeline-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`}><ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Pipeline</span></button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.sally} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const d = PIPELINE_DATA;
  return (
    <div className="flex flex-col h-full" data-testid="pipeline-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Kanban className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Pipeline</p>
          </div>
          {isDavid && (
            <button
              onClick={() => setOverviewDD(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
              data-testid="pipeline-deep-dive-btn"
            >
              <Search className="w-3.5 h-3.5" /> Deep Dive
            </button>
          )}
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        {/* Summary row */}
        <div className="grid grid-cols-6 gap-3">
          {Object.entries(d.summary).map(([key, val]) => {
            const labels = { totalPipeline: 'Total Pipeline', weightedPipeline: 'Weighted', winRate: 'Win Rate', avgCycle: 'Avg. Cycle', forecastAccuracy: 'Forecast Acc.', avgDealSize: 'Avg. Deal' };
            return (
              <div key={key} className={`${cardBg} border ${border} rounded-xl px-3 py-2.5 text-center`}>
                <p className={`text-[9px] ${ts}`}>{labels[key]}</p>
                <p className={`text-base font-bold mt-0.5 ${tp}`}>{val}</p>
              </div>
            );
          })}
        </div>

        {/* Stage Funnel */}
        <div className={`${cardBg} border ${border} rounded-xl p-4`}>
          <p className={`text-[11px] font-semibold mb-3 ${tp}`}>Pipeline by Stage</p>
          <StageFunnel stages={d.stages} isDark={isDark} />
        </div>

        {/* Forecast + Lost Reasons */}
        <div className="grid grid-cols-2 gap-4">
          <div className={`${cardBg} border ${border} rounded-xl p-4 relative`}>
            <ForecastBar forecast={d.forecast} isDark={isDark} />
            {isDavid && (
              <button
                onClick={() => setForecastDD(true)}
                className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-500/10'}`}
                data-testid="forecast-deepdive-btn"
              >
                <Search className="w-3 h-3" /> Deep Dive
              </button>
            )}
          </div>
          <div className={`${cardBg} border ${border} rounded-xl p-4 relative`}>
            <div className="flex items-center justify-between mb-3">
              <p className={`text-[11px] font-semibold ${tp}`}>Loss Reasons</p>
              {isDavid && (
                <button
                  onClick={() => setLossReasonsDD(true)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-500/10'}`}
                  data-testid="lossreasons-deepdive-btn"
                >
                  <Search className="w-3 h-3" /> Deep Dive
                </button>
              )}
            </div>
            <LostReasonsChart reasons={d.lostReasons} isDark={isDark} />
          </div>
        </div>

        {/* Velocity metrics */}
        <div>
          <p className={`text-xs font-semibold mb-3 ${tp}`}>Velocity Metrics</p>
          <div className="grid grid-cols-6 gap-3">
            {d.velocity.map((v, i) => <VelocityCard key={i} item={v} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SalesPipeline;
